import type { CmsAdapter, CmsPage } from '@eneco/cms-adapter';

/**
 * The `<title>` for a CMS page.
 *
 * Sitecore's "Browser Title" is the tab title, and every generated page document carries it as
 * `browserTitle`. A document's own `title`, where it has one, is the editorial heading rather than
 * the tab title, so it is only a fallback.
 *
 * Page-document fields land in `root.content` (`cms-sanity/src/adapter.ts`'s `buildTree` keeps every
 * non-slot field there), which is why this reads from content rather than needing a separate query.
 */
export const pageTitle = (page: CmsPage, fallback = 'Eneco'): string => {
  const content = page.root.content as Record<string, unknown> | undefined;
  const seo = (content?.seo ?? {}) as Record<string, unknown>;

  for (const candidate of [seo.title, content?.browserTitle, content?.openGraphTitle, content?.title]) {
    if (typeof candidate === 'string' && candidate.trim().length > 0) return candidate.trim();
  }
  return fallback;
};

/** The `seo` object every page document carries (ADR-0054). Normalised, so preferred over the
 *  individual Sitecore fields it was derived from. */
interface PageSeo {
  title?: string;
  description?: string;
  image?: unknown;
  noIndex?: boolean;
}

export interface PageMeta {
  title: string;
  description?: string;
  canonical?: string;
  /** Only set when the page opts out; absent means the default `index, follow`. */
  robots?: string;
  openGraph: { title?: string; description?: string; image?: string };
}

const text = (...candidates: unknown[]): string | undefined => {
  for (const candidate of candidates) {
    if (typeof candidate === 'string' && candidate.trim().length > 0) return candidate.trim();
  }
  return undefined;
};

/**
 * Everything the document head needs, read off the page document.
 *
 * Page-document fields land in `root.content`, so all of this is already on the `CmsPage` — none of
 * it was being read, which left every page on this lane with a default title, no description, no
 * canonical, and (the one that matters) no `noindex` on pages the CMS marks as such.
 *
 * `seo` wins over the individual fields wherever both exist: it is the deliberate page contract, and
 * the loose fields are the Sitecore originals it was derived from. `canonicalUrl` and
 * `openGraphImage` go through the adapter because they are a link object and an image respectively,
 * not plain strings.
 */
export const pageMeta = (page: CmsPage, adapter: CmsAdapter, fallback = 'Eneco'): PageMeta => {
  const content = (page.root.content ?? {}) as Record<string, unknown>;
  const seo = (content.seo ?? {}) as PageSeo;

  const description = text(seo.description, content.metaDescription, content.openGraphDescription);
  const image = adapter.toImage(content.openGraphImage) ?? adapter.toImage(seo.image);

  const noIndex = seo.noIndex === true || content.doNotIndex === true;
  const noFollow = content.doNotFollow === true;
  const robots = [noIndex ? 'noindex' : undefined, noFollow ? 'nofollow' : undefined].filter(Boolean).join(', ');

  return {
    title: pageTitle(page, fallback),
    description,
    canonical: adapter.toLink(content.canonicalUrl)?.href,
    ...(robots ? { robots } : {}),
    openGraph: {
      title: text(content.openGraphTitle, seo.title, content.browserTitle),
      description: text(content.openGraphDescription, description),
      image: image?.src,
    },
  };
};
